import { Button, TextField } from "@mui/material";
import { useForm, Controller, ControllerRenderProps } from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { loginFormTheme } from "./theme";
import { useIsUserExist } from "@src/entities/login/model/hooks/useIsUserExist/useIsUserExist";
import { useAuthStore } from "@src/entities/login/model/store/authStore";
import { useEffect } from "react";
import { reachGoal } from "@src/shared/lib/analytics/yandex/reachGoal";
import { useYandexAutorization } from "@src/entities/login";
import { YM_GOALS } from "@src/shared/lib/analytics/yandex/constants";
import { useNotificationStore } from "@src/shared/components/Notification";

import styles from "./MainLogin.module.scss";

interface MainLoginFormProps {
  email: string;
}

export type AuthAlertState = {
  authAlert?: {
    type: "error" | "success" | "info" | "warning";
    message: string;
  };
};

export const MainLoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mutate: isUserExist } = useIsUserExist();
  const { isAuthenticated } = useAuthStore();
  const { refetch, isLoading } = useYandexAutorization();
  const { showAlert } = useNotificationStore();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<MainLoginFormProps>({
    defaultValues: {
      email: "",
    },
    mode: "onTouched",
  });

  useEffect(() => {
    reachGoal(YM_GOALS.LOGIN_FORM_OPEN);
  }, []);

  //Временная мера отключить self-service регистрацию для B2C пользователей VNGPDL-1257
  useEffect(() => {
    const state = location.state as AuthAlertState | null;
    const authAlert = state?.authAlert;

    if (!authAlert) return;

    showAlert(authAlert.message, authAlert.type);

    navigate(location.pathname, {
      replace: true,
      state: null,
    });
  }, [location.pathname, location.state, navigate, showAlert]);

  const onSubmit = (data: MainLoginFormProps) => {
    isUserExist(
      { email: data.email },
      {
        onSuccess: (dataUserExist) => {
          reachGoal(YM_GOALS.LOGIN_SUBMIT);

          if (dataUserExist.data.exists) {
            navigate(`/autorization?login=${data.email}`);
          } else {
            //Временная мера отключить self-service регистрацию для B2C пользователей VNGPDL-1257
            // navigate(`/registration?login=${data.email}`);

            showAlert(
              "Пользователь не найден. Оставьте заявку на демо",
              "error",
            );
          }
        },
      },
    );
  };

  if (isAuthenticated) {
    return <Navigate to="/" />;
  }

  const loginWithYandex = async () => {
    reachGoal(YM_GOALS.YANDEX_LOGIN);

    const { data } = await refetch();

    if (data?.data?.confirmation_url) {
      window.location.href = data.data.confirmation_url;
    }
  };
  //путь до формы на лендинге демо
  const handleClickDemoBtn = () =>
    window.open("https://st-01.naimus.ai/questionnaire", "_blank");

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <Controller
          name="email"
          control={control}
          rules={{
            required: "Поле обязательно для заполнения",
            pattern: {
              value: /^[\w-\\.]+@([\w-]+\.)+[\w-]{2,4}$/g,
              message: "Неверный формат email",
            },
          }}
          render={({
            field,
          }: {
            field: ControllerRenderProps<MainLoginFormProps, "email">;
          }) => (
            <TextField
              {...field}
              label="Почта"
              variant="outlined"
              fullWidth
              error={!!errors.email}
              helperText={errors.email?.message}
              className={styles.input}
              InputProps={{
                style: {
                  borderRadius: 24,
                  color: "#fff",
                },
              }}
              InputLabelProps={{
                style: { color: "#ffffff83" },
              }}
              sx={{
                "& input:-webkit-autofill": {
                  WebkitTextFillColor: "#fff",
                  caretColor: "#fff",
                  transition: "background-color 5000s ease-in-out 0s",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#ffffff83",
                  },
                  "&:hover fieldset": {
                    borderColor: "#ffffffe0",
                  },

                  "&.Mui-focused fieldset": {
                    borderColor: "#ffffffe0",
                  },
                },
              }}
            />
          )}
        />

        <Button
          disabled={!isValid || isLoading}
          type="submit"
          variant="contained"
          className={styles.button}
          sx={loginFormTheme.button}
        >
          {/* Войти / Зарегистрироваться */}
          Войти
        </Button>
        <Button
          type="button"
          variant="contained"
          className={styles.button}
          sx={loginFormTheme.button}
          onClick={loginWithYandex}
          disabled={isLoading}
        >
          Войти через Яндекс
        </Button>
        <Button
          type="button"
          variant="contained"
          className={styles.button}
          sx={loginFormTheme.button}
          onClick={handleClickDemoBtn}
          disabled={isLoading}
        >
          Оставить заявку на демо
        </Button>
      </form>
    </>
  );
};
